/**
 * walkReading.ts — which language a walk is read in, and the walk in it.
 *
 * A walk is written in its city's language, and from 26 Sep 2026 most carry an
 * English translation too. A reader of a Nordic language reads a Nordic walk
 * in the original; everyone else gets English where it exists. The panel can
 * switch between the two. Used by CuratedWalkDetail; quotes are never
 * translated, because they are the register's own words.
 */
import type { CuratedWalk, Lang } from "../types/index.ts";

const NORDIC: ReadonlySet<string> = new Set(["da", "sv", "no"]);

/**
 * The language the address promises, on the walk's own page. /s/walk/<id> is
 * the original and /s/walk/<id>?lang=<l> each translation, each with its own
 * canonical and hreflang (api/og.ts). The panel used to follow the browser
 * instead, so Googlebot, which reads in English, rendered the Norwegian page
 * of Telthusbakken in English under a Norwegian title: a copy of the English
 * page (measured 1 Oct 2026). Null anywhere else, where the reader decides.
 */
// The address the visitor landed on, read when App.tsx first imports this
// module. The app rewrites /s/walk/<id> to /#walk=<id> as soon as the walk
// opens, before the panel asks, so the live location says "/" by then.
const LANDING: { pathname: string; search: string } | undefined =
  typeof window === "undefined" ? undefined : { pathname: window.location.pathname, search: window.location.search };

export function pageLang(walk: CuratedWalk, loc: { pathname: string; search: string } | undefined = LANDING): Lang | null {
  if (!loc || decodeURIComponent(loc.pathname) !== `/s/walk/${walk.id}`) return null;
  const asked = new URLSearchParams(loc.search).get("lang") as Lang | null;
  return asked && asked !== walk.textLang && walk.translations?.[asked] ? asked : walk.textLang;
}

/** The language to show `walk` in for a reader whose interface is `ui`. */
export function readingLang(walk: CuratedWalk, ui: Lang): Lang {
  const page = pageLang(walk);
  if (page) return page;
  if (walk.textLang === ui) return ui;
  if (walk.translations?.[ui]) return ui;
  if (NORDIC.has(ui) && NORDIC.has(walk.textLang)) return walk.textLang;
  if (walk.translations?.en) return "en";
  return walk.textLang;
}

/** `walk` with title, intro, and each stop's text and kind in `to`. */
export function walkIn(walk: CuratedWalk, to: Lang): CuratedWalk {
  const tr = to === walk.textLang ? undefined : walk.translations?.[to];
  if (!tr) return walk;
  return {
    ...walk,
    title: tr.title,
    intro: tr.intro,
    textLang: to,
    stops: walk.stops.map((s) => {
      const st = tr.stops[s.id];
      return st ? { ...s, text: st.text, kind: st.kind ?? s.kind } : s;
    }),
  };
}
